import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { FileText, Sparkles, Loader2, Download, Trash2, Eye } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { REPORT_STATUSES } from "./AnalyseBlocks";

const API = process.env.REACT_APP_BACKEND_URL;
const PERIOD_CHOICES = [["month", "Rapport mensuel"], ["3m", "3 derniers mois"], ["6m", "6 derniers mois"]];
const PERIOD_CHIP = { month: "Mensuel", "3m": "3 mois", "6m": "6 mois" };

export function AnalyseRapports({ clientId, clients = [] }) {
  const navigate = useNavigate();
  const [reports, setReports] = useState([]);
  const [period, setPeriod] = useState("month");
  const [month, setMonth] = useState(format(new Date(), "yyyy-MM"));
  const [generating, setGenerating] = useState(false);

  const load = () => {
    const url = clientId ? `/clients/${clientId}/reports` : "/reports";
    api.get(url).then((r) => setReports(r.data || [])).catch(() => {});
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [clientId]);

  const generate = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post(`/clients/${clientId}/reports/generate`, { month, period });
      toast.success("Rapport généré avec insights IA — statut « Généré »");
      load();
      navigate(`/cm/rapport/${data.id}`);
    } catch (e) { toast.error(apiError(e)); }
    finally { setGenerating(false); }
  };

  const remove = async (r) => {
    if (!window.confirm(r.status === "publie"
      ? "Supprimer ce rapport ? Il sera aussi retiré de l'espace client."
      : "Supprimer ce rapport ?")) return;
    try {
      await api.delete(`/reports/${r.id}`);
      toast.success("Rapport supprimé");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const clientName = (r) => r.client_name || (clients.find((c) => c.id === r.client_id) || {}).name || "—";

  return (
    <div className="space-y-3" data-testid="analyse-rapports">
      {clientId && (
        <div className="bg-white border border-[#E2E8F0] rounded-2xl shadow-sm px-4 py-3 flex flex-wrap items-end gap-3" data-testid="analyse-rapports-generate">
          <label className="flex flex-col gap-0.5">
            <span className="text-[10px] font-semibold text-neutral-400">Période du rapport</span>
            <select value={period} onChange={(e) => setPeriod(e.target.value)} data-testid="rapport-period"
              className="text-xs border border-[#E2E8F0] rounded-lg px-2.5 py-2 bg-white text-neutral-700">
              {PERIOD_CHOICES.map(([id, l]) => <option key={id} value={id}>{l}</option>)}
            </select>
          </label>
          <label className="flex flex-col gap-0.5">
            <span className="text-[10px] font-semibold text-neutral-400">Mois de fin</span>
            <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="rapport-month"
              className="text-xs border border-[#E2E8F0] rounded-lg px-2.5 py-2 bg-white text-neutral-700" />
          </label>
          <button onClick={generate} disabled={generating} data-testid="rapport-generate"
            className="inline-flex items-center gap-1.5 bg-[#0B132B] text-white rounded-xl px-4 py-2 text-xs font-semibold hover:bg-[#1C2A4F] transition-colors disabled:opacity-50">
            {generating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
            {generating ? "Génération IA…" : "Générer un rapport"}
          </button>
          <p className="text-[11px] text-neutral-400 ml-auto">
            Brouillon automatique le 1er de chaque mois — la publication au client reste manuelle.
          </p>
        </div>
      )}

      <div className="bg-white border border-[#E2E8F0] rounded-2xl shadow-sm overflow-hidden" data-testid="analyse-rapports-list">
        <p className="px-4 py-2.5 border-b border-neutral-100 font-heading font-bold text-[13px] text-[#0F172A]">
          Rapports {reports.length > 0 && <span className="text-[10px] text-neutral-400 bg-neutral-100 rounded-full px-1.5 py-0.5 ml-1">{reports.length}</span>}
        </p>
        {reports.length === 0 && (
          <p className="px-4 py-8 text-[13px] text-neutral-400 text-center" data-testid="analyse-rapports-empty">
            {clientId ? "Aucun rapport pour ce client — générez le premier." : "Aucun rapport généré pour le moment."}
          </p>
        )}
        {reports.map((r) => {
          const st = REPORT_STATUSES[r.status] || REPORT_STATUSES.genere;
          return (
            <div key={r.id} className="flex flex-wrap items-center gap-3 px-4 py-2.5 border-b border-neutral-100 last:border-0"
              data-testid={`rapport-row-${r.id}`}>
              <FileText className="w-4 h-4 text-neutral-400 shrink-0" />
              <div className="min-w-0 flex-1">
                <p className="text-[13px] font-semibold text-[#0F172A] capitalize truncate">
                  {clientName(r)} — <span className="normal-case">{r.period_label}</span>
                </p>
                <p className="text-[10px] text-neutral-400">
                  {PERIOD_CHIP[r.period] || "Mensuel"} · créé le {r.created_at ? new Date(r.created_at).toLocaleDateString("fr-FR") : "—"} par {r.created_by}
                  {r.auto && " · automatique"}
                  {r.published_at && ` · publié le ${new Date(r.published_at).toLocaleDateString("fr-FR")}`}
                </p>
              </div>
              <span className={`text-[9px] font-semibold rounded-full px-2 py-0.5 border ${st.cls}`} data-testid={`rapport-status-${r.id}`}>{st.label}</span>
              <span className="flex items-center gap-1 shrink-0">
                <button onClick={() => navigate(`/cm/rapport/${r.id}`)} data-testid={`rapport-open-${r.id}`}
                  className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#0369A1] hover:underline px-1.5 py-1">
                  <Eye className="w-3.5 h-3.5" /> Consulter
                </button>
                <a href={`${API}/api/reports/${r.id}/pdf`} target="_blank" rel="noreferrer" data-testid={`rapport-pdf-${r.id}`}
                  className="inline-flex items-center gap-1 text-[11px] font-semibold text-neutral-500 hover:text-neutral-900 px-1.5 py-1">
                  <Download className="w-3.5 h-3.5" /> PDF
                </a>
                <button onClick={() => remove(r)} data-testid={`rapport-delete-${r.id}`}
                  className="text-neutral-300 hover:text-red-500 transition-colors px-1.5 py-1">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
